import {
  QuoteAttributeDefaultDataProvider,
  QuoteAttributesDataProvider,
  QuoteDefaultsDataProvider,
  QuoteFrameBuyInItem,
  QuoteFrameItemPrice,
  QuoteItemsProvider,
  ResultProcessQuoteFrameItem
} from './quote-interface.js';
import { QuoteItemFrameAttributeTemplateProvider } from '../../../scriptsV6/v6config/v6-quote-item/v6-events.js';
import type { ApiProvider, NullPromise, ShowErrorEvent, StringDictionary } from '../types/misc.js';
import type { Frame, FrameAttributeGroup, FrameBuyInItem } from './frame-data.js';
import type { LitModalScreenBuilder } from './modal-factory-lit.js';
import type { ModalScreenBuilder } from './modal-factory.js';
import type { QCQuoteValues } from './quote-calculations.js';
import type { QuoteContract } from './quote-contract.js';
import type { QuoteShippingInformation } from './shipping-information.js';
import type { TemplateResult } from 'lit';

export interface V6QuoteDefaultsEditor {
  applyQuoteDefaultsToItems: () => Promise<void>;
  resetQuoteDefaults: () => Promise<void>;
  prepareForSave: () => Promise<boolean>;
  ui: HTMLElement;
  dataProvider?: QuoteDefaultsDataProvider;
  releaseResources: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface V6QuoteAttributesEditor {
  refresh: () => Promise<void>;
  applyChanges: () => Promise<void>;
  resetQuoteDefaults: () => Promise<void>;
  resetChanges: () => Promise<void>;
  prepareForSave: () => Promise<boolean>;
  ui: HTMLElement;
  dataProvider?: QuoteDefaultsDataProvider;
  releaseResources: () => Promise<void>;
}

/*
Defines the scope of the template as to where it can be seen and reused
 */
export enum TemplateScope {
  quote = 0,
  user = 1,
  tenant = 2
}

export enum TemplateContent {
  all = 0,
  defaults = 1,
  globals = 2
}

export interface QuoteIGUDataProvider {
  /**
   * external provider would pass in the supplier id. eg Quote.SupplierId
   */
  supplierId: string;
  supplierReferenceOverrideKey?: string;

  /**
   * Should this viewer be readonly.. ie quote state, locked viewer etc.
   */
  readonly: boolean;
  /**
   * provide in the data that is the QuoteIGUs to map to the defaults editor for selecting in fills
   */
  quoteIGUs: unknown; // V6QuoteIGU[]
}

export interface V6QuoteIGUEditor {
  addIGU: () => Promise<boolean>;
  prepareForSave: () => Promise<boolean>;
  readonly ui: HTMLElement;
  dataProvider?: QuoteIGUDataProvider;
  readonly busy: boolean;
  releaseResources: () => Promise<void>;
}

export interface QuoteFrameProcessingDataProvider {
  /**
   * external provider would pass in the supplier id. eg Quote.SupplierId
   */
  supplierId: string;
  supplierReferenceOverrideKey: string;

  /**
   *
   * provide in the data that is the QuoteIGUs to map to the defaults editor for selecting in fills
   */
  quoteItem: unknown; // V6QuoteItem
  quoteIGUs: unknown; // V6QuoteIGU[]
  quantity: number;
  quoteDefaults: unknown; // V6AttributeGroup[]

  progress?: (caption: string) => Promise<void>;
}

export const v6FrameUserMode = {
  powerUser: 'POWERUSER',
  standardUser: 'STANDARDUSER'
};

export interface QuoteItemProvider {
  id: string;
  quantity: number;
  /**
   * provide in the data that is the QuoteIGUs to map to the defaults editor for selecting in fills
   */
  quoteItem: unknown; // V6QuoteItemExternalStorage
  thumbnailURL: string;
  fetchAsset: (assetType: string) => Promise<string | object | null>;
}

export interface QuoteFrameDataProvider extends QuoteItemProvider {
  /**
   * external provider would pass in the supplier id. eg Quote.SupplierId
   */
  supplierId: string;
  supplierReferenceOverrideKey: string;
  /**
   * Should this viewer be readonly.. ie quote state, locked viewer etc.
   */
  readonly: boolean;
  quoteIGUs: unknown; // V6QuoteIGU[]
  propertiesTemplate: () => TemplateResult;
  quoteDefaults: unknown; // V6AttributeGroup[]
  changeNotifier?: () => void;
  framePicker?: V6PickerFamilyButton;
  preSelectionFrameReference?: { reference: string; description: string };
  customData?: () => { [key: string]: any };
  quoteAttributesTemplateProvider?: QuoteItemFrameAttributeTemplateProvider;

  /*
                        if this is provided it superceeds quoteDefaults and enables functionality that can update the defaults
                         */
  quoteAttributeDefaultDataProvider?: QuoteAttributeDefaultDataProvider;
}

export interface QuoteFreehandDataProvider {
  /**
   * external provider would pass in the supplier id. eg Quote.SupplierId
   */
  supplierId: string;
  quoteItemCommonId: string | null;
  supplierReferenceOverrideKey: string;
  quantity: number;
  singleUnitGrossSellPrice: number;
  itemSubType: number | null;
}

export interface V6QuoteFrameEditorData {
  price: QuoteFrameItemPrice;
  buyInItems: QuoteFrameBuyInItem[];
  quoteItem: unknown;
  //This is the mode required to edit this frame
  //either power or standard, based on what is edited during the session.
  requiredUserMode: string;
  thumbnail?: string;
  vrml?: string;
  bomView?: { [key: string]: any } | null;
}
export interface ModifiedAttribute {
  code: string;
  newValue: string | null;
  oldValue: string;
  isPowerUser: boolean;
}

export interface V6QuoteFrameEditor {
  readonly ui: HTMLElement;
  readonly busy: boolean;
  readonly updateComplete: Promise<void> | undefined | null;
  thumbnail: string;
  frameDescription: string;
  releaseResources: () => Promise<void>;
  commit: () => NullPromise<V6QuoteFrameEditorData>;
  resetEditorState: () => void;
  applyTopLevelValue: (code: string, value: string) => void;
  requestUpdate: () => void;
  modifiedAttributes: ModifiedAttribute[];
  changed: boolean;
}

export interface V6VrmlViewer {
  readonly ui: HTMLElement;
  releaseResources: () => Promise<void>;
}

export interface V6QuoteFrameBomViewer {
  readonly ui: HTMLElement;
  hasData: boolean;
  releaseResources: () => Promise<void>;
}

export interface V6PickerFamilyButton {
  caption: string;
  family: string;
  brandingPublicUrl: string;
  brandingCaption: string;
}

export interface V6CustomItemRequestType {
  name: string;
  note: string;
  objectReference: string;
}

export interface V6QuoteItemEditorServices {
  getQuoteDefaultsEditorService: (dataProvider: QuoteDefaultsDataProvider) => V6QuoteDefaultsEditor;
  getQuoteAttributesEditorService: (dataProvider: QuoteAttributesDataProvider) => V6QuoteAttributesEditor;
  getQuoteIGUEditorService: (dataProvider: QuoteIGUDataProvider) => V6QuoteIGUEditor;
  getQuoteFrameEditorService: (dataProvider: QuoteFrameDataProvider) => NullPromise<V6QuoteFrameEditor>;
  getQuoteFrameOptionsService: (dataProvider: QuoteFrameDataProvider) => NullPromise<V6QuoteFrameEditor>;
  getQuoteItemFreehandPrice: (dataProvider: QuoteFreehandDataProvider) => NullPromise<QuoteFrameItemPrice>;
  getFramePickerButtons: (supplierId: string) => NullPromise<V6PickerFamilyButton[]>;
  getBomViewer: (dataProvider: QuoteItemProvider) => NullPromise<V6QuoteFrameBomViewer>;
  runVrmlViewer: (dataProvider: QuoteItemsProvider) => Promise<void>;
}

export type EventAsync<TResult> = (() => Promise<TResult>) | Promise<TResult>;
export type EventAsyncExecutor<TResult> = (event: EventAsync<TResult>) => NullPromise<TResult>;

export interface V6ConfigEventHandlers {
  setLangHandler: (
    aEvent: (template: TemplateStringsArray, ...args: unknown[]) => string,
    langEvent: (text: string, namedResource?: string, doNotAddMissing?: boolean) => string
  ) => void;
  setErrorDialogEventHandler: (handler: ShowErrorEvent) => void;
  setApiInjector: (apiInjector: ApiProvider) => void;
  setModalScreenFactory: (aEvent: ModalScreenBuilder, aEvent1: LitModalScreenBuilder) => void;
  setUILockAndExecuteEvent: (event: EventAsyncExecutor<unknown>) => void;
  setUserMode: (userMode: string) => void;
  setApiAdditionalData: (data?: StringDictionary) => void;
}

export interface V6FrameSize {
  width?: number;
  displayWidth?: string;
  height?: number;
  displayHeight?: string;
  displayHeightFirst: boolean;
}

export interface V6QuoteItemSupplierInfo {
  supplierId: string;
  quoteItemVersion: string;
  isVersionCurrent: boolean;
}

export interface V6QuoteBrief {
  quoteCommonId: string;
  fullQuoteNumber: string;
  shippingLeadTime: number;
  deliveryDate: string;
  closedDate: string;
  status: string;
  notesFromSupplier: string;
}

// Interface for quote attributes configuration with triggers
export interface V6QuoteAttributesConfig {
  triggers: V6AttributeTrigger[];
}

export interface V6AttributeTrigger {
  /**
   * The attribute code that acts as a trigger
   */
  code: string;

  /**
   * Whether this attribute should trigger a server-side item calculation
   */
  runssi: boolean;

  /**
   * Whether this attribute should be excluded from validation
   */
  excludeFromValidation: boolean;
}

export interface V6DataUtilities {
  getFrameDimensions: (quoteItem: object /* V6QuoteItem */) => V6FrameSize;
  getCustomFrameDimensions: (quoteItem: object /* V6QuoteItem */) => string | undefined;
  getFrameData: (quoteItem: object /* V6QuoteItem */) => Frame;
  getQuoteDefaults: (quoteDefaults?: object /* V6AttributeGroup[] */) => FrameAttributeGroup[];
  getQuoteItemSupplier: (quoteItem: object /* V6QuoteItem */) => V6QuoteItemSupplierInfo;
  getV6SupplierQuoteInfo: (supplierId: string, quoteCommonId: string) => NullPromise<V6QuoteBrief>;
  getFrameBuyInItems: (quoteItem: object /* V6QuoteItem */) => FrameBuyInItem[];
  restartV6Service: (supplierId: string) => Promise<void>;
  getSupplierQuoteDefaults: (supplierId: string) => Promise<object>;
  getCustomItemRequestTypes: (supplierId: string) => Promise<V6CustomItemRequestType[]>;
  /*
   * Refresh the quote defaults for the supplier
   * @param supplierId The supplier id
   * @param quoteDefaults The quote defaults to refresh (V6AttributeGroup[])
   * @param quoteIGUs The quote IGUs to refresh (V6QuoteIGU[])
   * @returns The refreshed quote defaults (V6AttributeGroup[])
   */
  refreshQuoteDefaults: (supplierId: string, quoteDefaults: object, quoteIGUs: object) => NullPromise<object>;

  isQuoteIguReferenceValid: (quoteItem: object, quoteIGUS: object) => boolean;
  // Compare two quote items and return both the structured report and markdown
  getQuoteItemComparison: (
    header: string,
    beforeQuoteItem: object | null,
    afterQuoteItem: object | null,
    options?: CompareOptions
  ) => {
    report: CompareReport | null;
    markdown: string;
  } | null;
}

export enum V6OfflineCause {
  Connection = 0,
  Upgrading = 1,
  VersionMismatch = 2,
  InvalidV6ClientCode = 3
}

export interface SupplierInfo {
  id: string;
  description: string;
  online: boolean;
  offlineCause?: V6OfflineCause;
  offlineMessage?: string;
  version: string;
  isMetric: boolean;
  instanceId: string;
  configVersion: string;
  options: StringDictionary | null;
  customOptions: Record<string, any> | null;
}

export interface RequestGetSSI {
  quote: QuoteContract;
  shippingInformation: QuoteShippingInformation;
  supplierReferenceOverrideKey: string;
  additionalData: StringDictionary | null;
}

export interface V6QuoteItemPrice {
  qty: number;
  quoteItemCommonId: string;
  costPrice: number;
  costTax: number;
  overHead: number;
  markup: number;
  grossSellPrice: number;
  itemDisc: number;
  salesCommission: number;
  priceAdj: number;
  netSellPrice: number;
  tax: number;
  costPriceLab: number;
  costTaxLab: number;
  overHeadLab: number;
  markupLab: number;
  grossSellPriceLab: number;
  itemDiscLab: number;
  salesCommissLab: number;
  priceAdjLab: number;
  netSellPriceLab: number;
  taxLab: number;
  grossSellPriceLockAdj: number;
  grossSellPriceLockAdjLab: number;
  unRecoverableWaste: number;
  totalSellingPriceAdjusted: number;
  totalSellPrice: number;
}

export interface V6SSIPriceBreakdown {
  name: string;
  unitPrice: number;
  subType: number;
  sourceData: V6QuoteItemPrice | null;
}

export interface V6ServerSideSpecialItem {
  name: string;
  title: string;
  description: string;
  quantity: number;
  comment: string | null;
  quoteItemContentType: number;
  unitPrice: number;
  unitPriceBreakdown: V6SSIPriceBreakdown[] | null;
  sourceData: V6QuoteItemPrice | null;
  notes: V6SsiSupplierNote[] | null;
}

export const supplierNoteType = {
  clientWarranty: 'client-warranty',
  dealerNote: 'dealer-note',
  clientNote: 'client-note',
  clientDocument: 'client-document',
  dealerDocument: 'dealer-document',
  dealerWarranty: 'dealer-warranty'
} as const;

export interface V6SsiSupplierNote {
  noteType:
    | 'client-warranty'
    | 'dealer-note'
    | 'client-note'
    | 'client-document'
    | 'dealer-document'
    | 'dealer-warranty';
  quoteItemId: string | null;
  content: string;
  name: string;
}

export interface V6QuoteAnnotation {
  severity: 'information' | 'warning' | 'critical';
  content: string;
  relatedQuoteItemIds: string[] | null; // quote item ids
}

export const v6SupplierMarginMarkupValueTypes = {
  margin: 'margin',
  markup: 'narkup'
} as const;

export interface V6SupplierMarginMarkupOverride {
  value: number;
  valueType: 'markup' | 'margin';
}

export interface ResultGetSSI {
  items: V6ServerSideSpecialItem[];
  quoteCalculations: QCQuoteValues | null;
  marginMarkupOverride: V6SupplierMarginMarkupOverride | null;
  notes: V6SsiSupplierNote[] | null;
  annotations: V6QuoteAnnotation[] | null;
}

export interface V6Configurator {
  setSupplierReferenceOverrideKey: (supplierReferenceOverrideKey: string) => void;
  suppliers: (isVersionCritical?: boolean) => Promise<SupplierInfo[] | null>;
  isSupplierOnline: (supplierId: string) => Promise<boolean>;
  performSupplierHealthCheckLazy: () => Promise<void>;
  performSupplierHealthCheck: () => Promise<void>;
  getSupplier: (supplierId: string) => SupplierInfo;
  /**
   *
   * @param dataProvider
   * @param updatePrice if left undefined value is true
   * @returns
   */
  processV6QuoteItem: (
    dataProvider: QuoteFrameProcessingDataProvider,
    updatePrice?: boolean
  ) => Promise<ResultProcessQuoteFrameItem | null>;
  getQuoteSSI: (input: RequestGetSSI) => Promise<ResultGetSSI>;
}

export interface V6ConfigEntryPoint {
  events: V6ConfigEventHandlers;
  config: V6Configurator;
  util: V6DataUtilities;
  editors: V6QuoteItemEditorServices;
}

// Comparison interfaces used by the V6 quote comparator
export interface CompareOptions {
  excludePrice?: boolean; // if true, skip comparing price objects
  excludeQuoteLevelOptions?: boolean; // if true, skip comparing defaultQuoteOptions
}

// Generic before/after value pair
export interface BeforeAfter<T = string | null> {
  before: T | null;
  after: T | null;
}

// Attribute level diff
export interface AttributeDiff {
  code: string;
  label?: string | null;
  value: BeforeAfter<string>;
  displayValue: BeforeAfter<string | null>;
  status: 'added' | 'removed' | 'changed' | 'unchanged';
}

// Per-quote-item differences
export interface QuoteItemDiff {
  quoteItemCommonId: string | null;
  title?: BeforeAfter<string | null>;
  description?: BeforeAfter<string | null>;
  comments?: BeforeAfter<string | null>;
  productLine?: BeforeAfter<string | null>;
  frameDataDescription?: BeforeAfter<string | null>;
  // grouped attribute diffs
  quoteOptions?: AttributeDiff[]; // attributes coming from quoteItem.quoteItemOptions
  frameOptions?: AttributeDiff[]; // attributes coming from the root frame
  nestedFrames?: {
    objectReference: string | null;
    boundaryName?: string | null;
    description?: string | null;
    attributes: AttributeDiff[];
    nested?: any[]; // recursive (kept loose to avoid very verbose typing here)
  }[];
  annotationIssues?: BeforeAfter<any>;
  validationIssues?: BeforeAfter<any>;
  price?: BeforeAfter<any> | null; // only when not excluded
}

export interface QuoteLevelDiff {
  defaultQuoteOptions?: AttributeDiff[]; // flattened attributes
  defaultQuoteOptionsMeta?: BeforeAfter<any> | null; // placeholder if needed later
}

export interface CompareReport {
  quoteId?: BeforeAfter<string | null>;
  title?: BeforeAfter<string | null>;
  quoteLevel?: QuoteLevelDiff | null;
  items: QuoteItemDiff[];
}
